import { AlertCircle, BookOpen, ListTree, RefreshCw, TriangleAlert } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useOrganizationsCatalog } from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { downloadCsv } from "@/shared/utils/csv";

import { LedgerAccountRollup } from "../components/LedgerAccountRollup";
import { LedgerEntryCard } from "../components/LedgerEntryCard";
import { LedgerInvariantStrip } from "../components/LedgerInvariantStrip";
import { LedgerOperationHeader } from "../components/LedgerOperationHeader";
import { LedgerOperationPicker } from "../components/LedgerOperationPicker";
import { LedgerStatusTrail } from "../components/LedgerStatusTrail";
import { LEDGER_TYPE_BY_KIND, isLedgerOperationKind, isUuid } from "../constants/paymentsLedger.constants";
import { useLedgerLabels } from "../hooks/useLedgerLabels";
import { useLedgerOperation } from "../hooks/useLedgerOperation";
import { useLedgerOperationPicker } from "../hooks/useLedgerOperationPicker";
import { useOperationStatusTrail } from "../hooks/useOperationStatusTrail";
import type { LedgerOperationKind } from "../types/paymentsLedger.types";
import { buildLedgerCsv, ledgerCsvFilename } from "../utils/ledgerCsv";
import { buildLedgerPath } from "../utils/ledgerLinks";

/**
 * `/app/payments-ledger` — the double-entry journal of the payments runtime, for INTERNAL staff.
 *
 * The endpoint behind it answers by `operation_id` and by nothing else — there is no way to list journal
 * entries globally, by account or by date — so the page is a master/detail: pick an operation (or arrive with
 * its id in the URL) and read its entries. `/:operationKind/:operationId` makes that state linkable, which is
 * what the payments-activity drawer and the wallet statement point at.
 *
 * The route is `InternalOnly`-gated and the ledger endpoint is itself `userTypes: [INTERNAL]`; the RBAC half
 * (`payments:read`) is decided by the backend and a 401/403 latches into the no-access view — legacy
 * `scopes[]` are NOT consulted.
 *
 * Freshness: a terminal operation's journal is immutable, so it is read once. While the selected operation
 * can still move — a settle appends an entry — the pane refreshes every 15 s, the same cadence as the
 * payments activity list. There is no websocket for payments.
 */
const PaymentsLedgerPage = () => {
  const { t, activeLanguage } = useTranslations("paymentsLedger");
  const { t: tGlobal } = useTranslations("global");
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { operationKind, operationId } = useParams();

  useAppShellBreadcrumb(
    useMemo(
      () => [
        { label: tGlobal("sidebar_section_analitica") },
        { label: tGlobal("sidebar_menu_payments_ledger"), to: "/app/payments-ledger" },
      ],
      [tGlobal],
    ),
  );

  // A malformed deep link is treated as "nothing selected": the ledger endpoint 400s on a non-uuid, so it is
  // never asked.
  const selectedKind = isLedgerOperationKind(operationKind) ? operationKind : null;
  const selectedId = operationId && isUuid(operationId) ? operationId.toLowerCase() : null;
  const hasSelection = selectedKind !== null && selectedId !== null;

  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const picker = useLedgerOperationPicker(selectedKind ?? undefined);
  const organizationsCatalog = useOrganizationsCatalog();

  const organizationNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const organization of organizationsCatalog.organizations) map[organization.id] = organization.name;
    return map;
  }, [organizationsCatalog.organizations]);

  const labels = useLedgerLabels(organizationNames);

  const pickerCandidate = useMemo(
    () =>
      hasSelection
        ? (picker.candidates.find(
            (candidate) => candidate.kind === selectedKind && candidate.id === selectedId,
          ) ?? null)
        : null,
    [hasSelection, picker.candidates, selectedKind, selectedId],
  );

  // One hook for both reads: the journal needs only the id, and it is what reveals the organization a swap's
  // detail endpoint requires (a deep link does not carry it).
  const ledger = useLedgerOperation({
    kind: hasSelection ? selectedKind : null,
    operationId: selectedId,
    pickerCandidate,
  });

  const candidate = ledger.candidate;

  // Re-read the transition log whenever the operation's status moves, so the two halves stay in step.
  const trail = useOperationStatusTrail(
    hasSelection ? LEDGER_TYPE_BY_KIND[selectedKind] : null,
    selectedId,
    candidate?.status ?? null,
  );

  const organizationId = candidate?.organizationId ?? ledger.view?.organizationIds[0] ?? null;
  const organizationName = organizationId ? (organizationNames[organizationId] ?? null) : null;

  const handleSelect = useCallback(
    (kind: LedgerOperationKind, id: string) => {
      setIsPickerOpen(false);
      navigate(buildLedgerPath(kind, id));
    },
    [navigate],
  );

  const handleRefresh = useCallback(() => {
    ledger.refresh();
    void picker.refresh();
  }, [ledger, picker]);

  const handleExportCsv = useCallback(() => {
    if (!ledger.view || !selectedKind || !selectedId) return;
    const csv = buildLedgerCsv(ledger.view, { account: labels.account, entry: labels.entry });
    downloadCsv(csv, ledgerCsvFilename(selectedKind, selectedId));
  }, [labels, ledger.view, selectedId, selectedKind]);

  // A 401/403 on either read is terminal for this surface — both hit the same `payments:read` role.
  if (!picker.hasAccess || !ledger.hasAccess) return <NoAccessView />;

  const pickerPane = (
    <LedgerOperationPicker
      picker={picker}
      organizations={organizationsCatalog.organizations}
      organizationsDisabled={
        organizationsCatalog.isLoading || organizationsCatalog.accessDenied || organizationsCatalog.hasError
      }
      organizationNames={organizationNames}
      selectedId={selectedId}
      onSelect={handleSelect}
    />
  );

  return (
    <div className="space-y-6 p-2">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_subtitle")}
        actions={
          <>
            {isMobile && (
              <ColocaButton variant="default" size="sm" icon={ListTree} onClick={() => setIsPickerOpen(true)}>
                {t("picker_open_button")}
              </ColocaButton>
            )}
            <div className="flex items-center gap-2">
              {ledger.lastUpdatedAt && (
                <span className="font-mono text-xs text-muted-foreground tabular-nums" aria-live="polite">
                  {t("live_updated_at", {
                    time: ledger.lastUpdatedAt.toLocaleTimeString(activeLanguage, {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    }),
                  })}
                </span>
              )}
              <ColocaButton
                variant="default"
                size="sm"
                icon={RefreshCw}
                aria-label={t("refresh_aria")}
                className={cn((ledger.isRefreshing || picker.isRefreshing) && "[&_svg]:animate-spin")}
                onClick={handleRefresh}
              >
                {t("refresh_button")}
              </ColocaButton>
            </div>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        {!isMobile && <aside className="h-fit xl:sticky xl:top-4">{pickerPane}</aside>}

        <main className="min-w-0 space-y-4">
          {!hasSelection ? (
            <ColocaEmpty icon={BookOpen} title={t("empty_title")} description={t("empty_description")}>
              {isMobile && (
                <ColocaButton
                  variant="primary"
                  size="sm"
                  icon={ListTree}
                  onClick={() => setIsPickerOpen(true)}
                >
                  {t("picker_open_button")}
                </ColocaButton>
              )}
            </ColocaEmpty>
          ) : (
            <>
              <LedgerOperationHeader
                kind={selectedKind}
                operationId={selectedId}
                candidate={candidate}
                organizationName={organizationName}
                view={ledger.view}
                isStatusUnavailable={ledger.isEntityUnavailable}
                onExportCsv={handleExportCsv}
                canExport={(ledger.view?.entries.length ?? 0) > 0}
              />

              {ledger.hasError ? (
                <ColocaAlert
                  variant="destructive"
                  icon={AlertCircle}
                  heading={t("error_load")}
                  text={t("error_load_detail")}
                  actions={
                    <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={handleRefresh}>
                      {t("error_retry")}
                    </ColocaButton>
                  }
                />
              ) : ledger.isLoading ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {[0, 1, 2, 3].map((index) => (
                      <ColocaSkeleton key={index} className="h-[5.5rem] rounded-xl" />
                    ))}
                  </div>
                  <ColocaSkeleton className="h-56 rounded-xl" />
                  <ColocaSkeleton className="h-40 rounded-xl" />
                </div>
              ) : !ledger.view || ledger.view.entries.length === 0 ? (
                <ColocaEmpty
                  icon={BookOpen}
                  title={t("no_entries_title")}
                  description={t("no_entries_description")}
                />
              ) : (
                <>
                  {/* The runtime asserts this invariant before writing, so a mismatch is never cosmetic. */}
                  {!ledger.view.isBalanced && (
                    <ColocaAlert
                      variant="destructive"
                      icon={TriangleAlert}
                      heading={t("unbalanced_heading")}
                      text={t("unbalanced_text")}
                    />
                  )}

                  <LedgerInvariantStrip view={ledger.view} />

                  <LedgerStatusTrail
                    changes={trail.changes}
                    isLoading={trail.isLoading}
                    hasError={trail.hasError}
                  />

                  <section className="space-y-3">
                    <div>
                      <h2 className="text-sm font-semibold text-foreground">{t("entries_title")}</h2>
                      <p className="mt-0.5 text-xs text-muted-foreground">{t("entries_description")}</p>
                    </div>
                    {ledger.view.entries.map((entry) => (
                      <LedgerEntryCard
                        key={entry.entryId}
                        entry={entry}
                        labels={labels}
                        isMobile={isMobile}
                      />
                    ))}
                  </section>

                  <section className="space-y-3">
                    <div>
                      <h2 className="text-sm font-semibold text-foreground">{t("rollup_title")}</h2>
                      <p className="mt-0.5 text-xs text-muted-foreground">{t("rollup_description")}</p>
                    </div>
                    <LedgerAccountRollup rows={ledger.view.rollup} labels={labels} isMobile={isMobile} />
                  </section>

                  <ColocaAlert variant="secondary" icon={BookOpen} text={t("why_banner")} />
                </>
              )}
            </>
          )}
        </main>
      </div>

      {isMobile && (
        <ColocaDrawer
          open={isPickerOpen}
          onOpenChange={setIsPickerOpen}
          side="left"
          size="lg"
          title={t("picker_title")}
          description={t("picker_description")}
        >
          {pickerPane}
        </ColocaDrawer>
      )}
    </div>
  );
};

export default PaymentsLedgerPage;
